/**
 * AdminNetworkClubManager Component
 * Admin tools voor het beheren van netwerkclub aanvragen
 */

import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  CheckCircle, 
  XCircle, 
  Clock, 
  MapPin, 
  Users, 
  ExternalLink,
  Search,
  Filter,
  RefreshCw,
  AlertTriangle,
  Eye,
  Trash2,
  Mail
} from 'lucide-react';
import { networkClubService } from '../../services/networkClubService';
import { NetworkClub, ClubModerationStatus } from '../../types/networkclub';
import { auth } from '../../firebase';

interface ClubStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
}

const AdminNetworkClubManager: React.FC = () => {
  const [clubs, setClubs] = useState<NetworkClub[]>([]);
  const [stats, setStats] = useState<ClubStats>({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState<ClubModerationStatus | 'all'>('pending');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClub, setSelectedClub] = useState<NetworkClub | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [allClubs, clubStats] = await Promise.all([
        networkClubService.getAllClubsForAdmin(),
        networkClubService.getClubStats()
      ]);
      setClubs(allClubs);
      setStats(clubStats);
    } catch (error) {
      console.error('Error loading clubs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  // Filter clubs
  const filteredClubs = clubs.filter(club => {
    // Filter by status
    if (filter !== 'all' && club.moderationStatus !== filter) {
      return false;
    }
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        club.name.toLowerCase().includes(term) ||
        club.city?.toLowerCase().includes(term) ||
        club.region.toLowerCase().includes(term) ||
        club.organizerName?.toLowerCase().includes(term) ||
        club.organizerEmail?.toLowerCase().includes(term)
      );
    }
    return true;
  });

  // Actions
  const handleApprove = async (club: NetworkClub) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    setActionLoading(club.id);
    try {
      const success = await networkClubService.approveClub(club.id, userId);
      if (success) {
        // Update local state
        setClubs(prev => prev.map(c => 
          c.id === club.id 
            ? { ...c, moderationStatus: 'approved' as ClubModerationStatus, isVerified: true }
            : c
        ));
        setStats(prev => ({
          ...prev,
          pending: prev.pending - 1,
          approved: prev.approved + 1
        }));
      }
    } catch (error) {
      console.error('Error approving club:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!selectedClub) return;
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    setActionLoading(selectedClub.id);
    try {
      const success = await networkClubService.rejectClub(selectedClub.id, userId, rejectReason);
      if (success) {
        // Update local state
        setClubs(prev => prev.map(c => 
          c.id === selectedClub.id 
            ? { ...c, moderationStatus: 'rejected' as ClubModerationStatus, moderationNote: rejectReason }
            : c
        ));
        setStats(prev => ({
          ...prev,
          pending: prev.pending - 1,
          rejected: prev.rejected + 1
        }));
        setShowRejectModal(false);
        setSelectedClub(null);
        setRejectReason('');
      }
    } catch (error) {
      console.error('Error rejecting club:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const openRejectModal = (club: NetworkClub) => {
    setSelectedClub(club);
    setShowRejectModal(true);
  };

  // Status badge
  const getStatusBadge = (status: ClubModerationStatus) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
            <Clock className="w-3 h-3" />
            Wacht op review
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
            <CheckCircle className="w-3 h-3" />
            Goedgekeurd
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
            <XCircle className="w-3 h-3" />
            Afgewezen
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-500">Clubs laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div 
          onClick={() => setFilter('all')}
          className={`bg-white rounded-xl p-4 border cursor-pointer transition-all ${
            filter === 'all' ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">Totaal</p>
            </div>
          </div>
        </div>

        <div 
          onClick={() => setFilter('pending')}
          className={`bg-white rounded-xl p-4 border cursor-pointer transition-all ${
            filter === 'pending' ? 'border-yellow-500 ring-2 ring-yellow-100' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
              <p className="text-sm text-gray-500">Pending</p>
            </div>
          </div>
        </div>

        <div 
          onClick={() => setFilter('approved')}
          className={`bg-white rounded-xl p-4 border cursor-pointer transition-all ${
            filter === 'approved' ? 'border-green-500 ring-2 ring-green-100' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.approved}</p>
              <p className="text-sm text-gray-500">Goedgekeurd</p>
            </div>
          </div>
        </div>

        <div 
          onClick={() => setFilter('rejected')}
          className={`bg-white rounded-xl p-4 border cursor-pointer transition-all ${
            filter === 'rejected' ? 'border-red-500 ring-2 ring-red-100' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.rejected}</p>
              <p className="text-sm text-gray-500">Afgewezen</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Alert */}
      {stats.pending > 0 && filter !== 'pending' && (
        <div 
          onClick={() => setFilter('pending')}
          className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:bg-yellow-100 transition-colors"
        >
          <AlertTriangle className="w-5 h-5 text-yellow-600" />
          <p className="text-sm text-yellow-800">
            <strong>{stats.pending} club{stats.pending > 1 ? 's' : ''}</strong> wacht{stats.pending === 1 ? '' : 'en'} op review
          </p>
        </div>
      )}

      {/* Search & Actions */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Zoek op naam, stad, organizer..."
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="text-sm">Vernieuwen</span>
        </button>
      </div>

      {/* Club List */}
      <div className="space-y-3">
        {filteredClubs.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="font-medium text-gray-700 mb-1">Geen clubs gevonden</h3>
            <p className="text-sm text-gray-500">
              {searchTerm 
                ? 'Probeer andere zoektermen'
                : filter === 'pending' 
                  ? 'Alle aanvragen zijn verwerkt 🎉'
                  : 'Er zijn nog geen clubs met deze status'}
            </p>
          </div>
        ) : (
          filteredClubs.map(club => (
            <div 
              key={club.id}
              className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start gap-4">
                {/* Logo */}
                <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold">
                    {club.name.substring(0, 2).toUpperCase()}
                  </span>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div>
                      <h3 className="font-semibold text-gray-900">{club.name}</h3>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mt-0.5">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {club.city || club.region}
                        </span>
                        {club.kvkNumber && (
                          <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            KVK: {club.kvkNumber}
                          </span>
                        )}
                      </div>
                    </div>
                    {getStatusBadge(club.moderationStatus)}
                  </div>

                  {club.description && (
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{club.description}</p>
                  )}

                  {/* Organizer Info */}
                  {(club.organizerName || club.organizerEmail) && (
                    <div className="mt-3 p-2 bg-gray-50 rounded-lg text-sm">
                      <p className="text-gray-600">
                        <strong>Aangemeld door:</strong> {club.organizerName || 'Onbekend'}
                        {club.organizerEmail && (
                          <span className="ml-2 text-gray-400">({club.organizerEmail})</span>
                        )}
                      </p>
                    </div>
                  )}

                  {/* Rejection Note */}
                  {club.moderationStatus === 'rejected' && club.moderationNote && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-100 rounded-lg text-sm">
                      <p className="text-red-700">
                        <strong>Reden:</strong> {club.moderationNote}
                      </p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-4">
                    {club.moderationStatus === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(club)}
                          disabled={actionLoading === club.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                        >
                          <CheckCircle className="w-4 h-4" />
                          Goedkeuren
                        </button>
                        <button
                          onClick={() => openRejectModal(club)}
                          disabled={actionLoading === club.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                        >
                          <XCircle className="w-4 h-4" />
                          Afwijzen
                        </button>
                      </>
                    )}

                    {club.website && (
                      <a
                        href={club.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 text-sm rounded-lg transition-colors"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Website
                      </a>
                    )}

                    {club.organizerEmail && (
                      <a
                        href={`mailto:${club.organizerEmail}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 text-sm rounded-lg transition-colors"
                      >
                        <Mail className="w-4 h-4" />
                        Email
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && selectedClub && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Club afwijzen</h3>
                  <p className="text-sm text-gray-500">{selectedClub.name}</p>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reden voor afwijzing (optioneel)
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Bijv. Ongeldige KVK gegevens, spam, etc."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setSelectedClub(null);
                    setRejectReason('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuleren
                </button>
                <button
                  onClick={handleReject}
                  disabled={actionLoading === selectedClub.id}
                  className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {actionLoading === selectedClub.id ? 'Bezig...' : 'Afwijzen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNetworkClubManager;
